import { FileImage, FileText } from "lucide-react";

import { cn } from "@/lib/utils";
import { useInputUploadSignedUrl } from "../../hooks/useInputUploadSignedUrl";
import { FileUploadPreview } from "@/features/inputs/components/FileUploadPreview";
import { isImageFileName, isPdfFileName } from "@/features/inputs/utils/fileUploadHelpers";
import { BaseEntryValue } from "./BaseEntryValue";
import type { BaseEntryValueProps } from "./BaseEntryValue";

export interface DocumentUploadEntryValueProps extends Omit<BaseEntryValueProps, "value"> {
    diaryId?: string;
    entryId: string;
    inputId: string;
    fileName: string | null;
    objectId: string | null;
    /** Pre-signed by the server for print/report rendering; skips the client query. */
    signedUrl?: string | null;
}

export function DocumentUploadEntryValue({
    diaryId,
    entryId,
    inputId,
    fileName,
    objectId,
    signedUrl: serverSignedUrl,
    inputLabel,
    size,
    className,
    ...props
}: DocumentUploadEntryValueProps) {
    const query = useInputUploadSignedUrl({
        diaryId,
        entryId,
        inputId,
        fileName,
        objectId,
        // The print page renders anonymously and cannot sign these itself.
        enabled: !serverSignedUrl,
    });

    const signedUrl = serverSignedUrl ?? query.data;
    const isLoading = !serverSignedUrl && query.isLoading;
    const isError = !serverSignedUrl && query.isError;

    const displayName = fileName?.trim() || "Uploaded file";
    const hasPreviewableFile = Boolean(fileName && (isImageFileName(fileName) || isPdfFileName(fileName)));

    return (
        <BaseEntryValue inputLabel={inputLabel} size={size} className={className} {...props}>
            <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                    <span className="flex flex-shrink-0 justify-center items-center bg-slate-100 rounded-md w-10 h-10">
                        {fileName && isPdfFileName(fileName) ? (
                            <FileText className="w-5 h-5 text-slate-700" aria-hidden="true" />
                        ) : (
                            <FileImage className="w-5 h-5 text-slate-700" aria-hidden="true" />
                        )}
                    </span>
                    <p
                        className={cn(
                            "min-w-0 font-medium text-slate-900 truncate",
                            size === "sm" && "text-sm",
                            size === "lg" && "text-base"
                        )}
                    >
                        {displayName}
                    </p>
                </div>

                {hasPreviewableFile || signedUrl ? (
                    <FileUploadPreview
                        fileName={displayName}
                        previewUrl={signedUrl}
                        isLoading={isLoading}
                        isError={isError}
                        size={size ?? "md"}
                        showActions={Boolean(signedUrl)}
                    />
                ) : null}
            </div>
        </BaseEntryValue>
    );
}
